import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';
import { defaultDateRange } from '../utils/vendorKpi';
import { DateFilters } from '../components/vendorMgmt/VendorKpiUi';

const RISK_COLORS = { High: '#EF4444', Medium: '#F59E0B', Low: '#10B981' };

function RiskBadge({ level }) {
  const styles = {
    High: 'bg-red-100 text-red-800 border-red-200',
    Medium: 'bg-amber-100 text-amber-800 border-amber-200',
    Low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${styles[level] || styles.Low}`}>
      {level}
    </span>
  );
}

function RiskDonut({ segments, total }) {
  const size = 100;
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const sum = segments.reduce((s, x) => s + x.count, 0) || 1;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E8EDF2" strokeWidth="12" />
        {segments.map((seg) => {
          const len = (seg.count / sum) * circ;
          const dash = `${len} ${circ - len}`;
          const el = (
            <circle
              key={seg.label}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={RISK_COLORS[seg.label] || '#94A3B8'}
              strokeWidth="12"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <span className="absolute text-xl font-bold text-sap-text">{total}</span>
    </div>
  );
}

function SummaryCard({ title, value, subtitle, tone }) {
  const ring = { High: 'border-red-200', Medium: 'border-amber-200', Low: 'border-emerald-200' };
  return (
    <div className={`bg-white rounded-xl border-2 ${ring[tone] || 'border-sap-border/60'} shadow-sm p-4 min-h-[100px]`}>
      <h3 className="text-xs font-semibold text-sap-textLight">{title}</h3>
      <div className="text-3xl font-light text-sap-text mt-1">{value}</div>
      {subtitle && <p className="text-[10px] text-sap-textLight mt-1">{subtitle}</p>}
    </div>
  );
}

function KPISection({
  number,
  title,
  subtitle,
  score,
  color = "green",
  children
}) {
  return (
    <div className="bg-white rounded-xl border border-sap-border shadow-sm p-4">

      <div className="flex justify-between mb-4">

        <div className="flex gap-3">

          <div className="w-7 h-7 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold">
            {number}
          </div>

          <div>
            <h3 className="font-semibold">
              {title}
            </h3>

            <p className="text-xs text-gray-500">
              {subtitle}
            </p>
          </div>

        </div>

        {score && (
          <div
            className={`text-3xl font-bold ${
              color === "green"
                ? "text-green-600"
                : color === "orange"
                ? "text-orange-500"
                : "text-red-500"
            }`}
          >
            {score}
          </div>
        )}
      </div>

      {children}

    </div>
  );
}

export default function VendorRiskManagement() {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);

  const [kpiGroup, setKpiGroup] = useState('All');
  const [riskLevel, setRiskLevel] = useState('All');

  /* ADD HERE */
  const [selectedCategory, setSelectedCategory] = useState('All');

  const [showRiskModal, setShowRiskModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState('');
  const [riskVendors, setRiskVendors] = useState([]);

  /*const vendorCategories = ['All','TATA','BAJAJ','MAHINDRA','ASHOK LEYLAND','DAIMLER','JBM','GODREJ','ROYAL ENFIELD'];*/
  const [vendorCategories, setVendorCategories] = useState(['All']);

  const [dashboardSummary, setDashboardSummary] = useState({});
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryTitle, setDeliveryTitle] = useState('');
  const [deliveryRows, setDeliveryRows] = useState([]);

  const [kpiGroups, setKpiGroups] = useState([]);
  const [summary, setSummary] = useState({ high: 0, medium: 0, low: 0, total: 0 });
  const [byLevel, setByLevel] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('risk_score');
  const [dir, setDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        kpi_group: kpiGroup,
        risk_level: riskLevel,
        page: String(page),
        limit: String(limit),
        sort,
        dir,
      });
      const data = await api.get(`/vendors/risk?${qs}`);
      setVendorCategories(['All',...(data.vendor_categories || [])]);
      setDashboardSummary(data.dashboard_summary || {});
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSummary(data.summary || {});
      setByLevel(data.by_level || []);
      setKpiGroups(data.kpi_groups || []);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, kpiGroup, riskLevel, page, limit, sort, dir]);

  useEffect(() => {
    dispatch({ type: 'SET_BREADCRUMB', payload: ['Home', 'Vendor Management', 'Vendor risk management'] });
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('desc'); }
  };

  const openRiskList = (risk) => {
    const filtered = rows.filter(
      (r) => String(r.Risk_Status || r.risk_level || '').toUpperCase().includes(risk.replace(' RISK', ''))
    );
    setRiskVendors(filtered);
    setSelectedRisk(risk);
    setShowRiskModal(true);
  };

  const openDeliveryList = async (type) => {
  try {
    const data = await api.get(
      `/vendors/delivery-vendors?type=${type}`
    );

    setDeliveryRows(data || []);

    setDeliveryTitle(
      type === 'ontime'
        ? 'On-Time Vendors'
        : 'Late Delivery Vendors'
    );

    setShowDeliveryModal(true);
  } catch (err) {
    console.error(err);
  }
};



  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-sap-text">Vendor Risk Management</h1>
          
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-sap-textLight">Risk level</span>
            <select
              value={riskLevel}
              onChange={(e) => { setRiskLevel(e.target.value); setPage(1); }}
              className="border border-sap-border rounded-lg px-3 py-1.5 text-sm min-w-[120px] bg-white"
            >
              <option value="All">All</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </label>
          <DateFilters
            startDate={startDate}
            endDate={endDate}
            onStartChange={(v) => { setStartDate(v); setPage(1); }}
            onEndChange={(v) => { setEndDate(v); setPage(1); }}
            kpiGroup={kpiGroup}
            kpiGroups={kpiGroups}
            onKpiGroupChange={(v) => { setKpiGroup(v); setPage(1); }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4 flex items-center gap-4">
          <RiskDonut segments={byLevel} total={summary.total || 0} />
          <ul className="space-y-1.5 text-[11px]">
            {byLevel.map((g) => (
              <li key={g.label} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RISK_COLORS[g.label] }} />
                <span className="flex-1">{g.label}</span>
                <span className="font-bold">{g.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div
  onClick={() =>navigate('/vendor-management/risk-list?risk=HIGH')}
  className="cursor-pointer"
>
  <SummaryCard
    title="High risk"
    value={summary.high ?? 0}
    subtitle="Click to view vendors"
    tone="High"
  />
</div>

<div
  onClick={() =>navigate('/vendor-management/risk-list?risk=MEDIUM')}
  className="cursor-pointer"
>
  <SummaryCard
    title="Medium risk"
    value={summary.medium ?? 0}
    subtitle="Click to view vendors"
    tone="Medium"
  />
</div>

<div
  onClick={() =>navigate('/vendor-management/risk-list?risk=LOW')}
  className="cursor-pointer"
>
  <SummaryCard
    title="Low risk"
    value={summary.low ?? 0}
    subtitle="Click to view vendors"
    tone="Low"
  />
</div>
        
      </div>

      
{/* Vendor Intelligence Dashboard */}

<div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">

  {/* 1 Vendor Category */}

  <KPISection
    number="1"
    title="Vendor Category"
    subtitle={`Risk classification - ${total} vendors`}
  >

   <div className="space-y-3">

  <div
    onClick={() =>
      navigate('/vendor-management/risk-list?category=Approved Vendor')
    }
    className="bg-green-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
  >
    <div className="text-2xl font-bold text-green-600">
      {dashboardSummary.approved || 0}
    </div>

    <div className="text-xs text-gray-600">
      Approved Vendor
    </div>
  </div>

  <div
    onClick={() =>
      navigate('/vendor-management/risk-list?category=Preferred Vendor')
    }
    className="bg-blue-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
  >
    <div className="text-2xl font-bold text-blue-600">
      {dashboardSummary.preferred || 0}
    </div>

    <div className="text-xs text-gray-600">
      Preferred Vendor
    </div>
  </div>

  <div
    onClick={() =>
      navigate('/vendor-management/risk-list?category=Conditional Vendor')
    }
    className="bg-orange-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
  >
    <div className="text-2xl font-bold text-orange-600">
      {dashboardSummary.conditional || 0}
    </div>

    <div className="text-xs text-gray-600">
      Conditional Vendor
    </div>
  </div>

  <div
    onClick={() =>
      navigate('/vendor-management/risk-list?category=Blacklisted Vendor')
    }
    className="bg-red-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
  >
    <div className="text-2xl font-bold text-red-600">
  {dashboardSummary.blacklisted || 0}
</div>

    <div className="text-xs text-gray-600">
      Blacklisted Vendor
    </div>
  </div>

</div> 
 
  </KPISection>

  {/* 2 Vendor Rating */}

  <KPISection
    number="2"
    title="Vendor Rating"
    subtitle="Composite weighted score"
    score={dashboardSummary.vendorRating ?? 0}
    color="orange"
  >

    <div className="space-y-4">

  {/* Vendor Rating KPIs */}

  <div className="grid grid-cols-2 gap-3">

    <div
      onClick={() =>
        navigate('/vendor-management/risk-list?risk=LOW')
      }
      className="bg-green-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
    >
      <div className="text-2xl font-bold text-green-600">
        {summary.low || 0}
      </div>

      <div className="text-xs text-gray-600">
        High Rated Vendors
      </div>
    </div>

    <div
      onClick={() =>
        navigate('/vendor-management/risk-list?risk=HIGH')
      }
      className="bg-red-50 rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition"
    >
      <div className="text-2xl font-bold text-red-600">
        {summary.high || 0}
      </div>

      <div className="text-xs text-gray-600">
        Low Rated Vendors
      </div>
    </div>

  </div>

  {/* Existing Score Bars */}

      {[
  {
    name: "Delivery",
    value: Number(dashboardSummary.deliveryScore || 0),
    color: "bg-red-500"
  },
  {
    name: "Quality",
    value: Number(dashboardSummary.qualityScore || 0),
    color: "bg-green-500"
  },
  {
    name: "Procurement",
    value: Number(dashboardSummary.procurementScore || 0),
    color: "bg-green-500"
  },
  {
    name: "Financial",
    value: Number(dashboardSummary.financialScore || 0),
    color: "bg-green-500"
  }
].map((item) => (

        <div key={item.name}>

          <div className="flex justify-between text-xs mb-1">
            <span>{item.name}</span>
            <span>{item.value}</span>
          </div>

          <div className="h-2 bg-gray-200 rounded">

            <div
              className={`h-2 rounded ${item.color}`}
              style={{ width: `${item.value}%` }}
            />

          </div>

        </div>

      ))}

    </div>

  </KPISection>

  {/* 3 Delivery Performance */}

  <KPISection
    number="3"
    title="Delivery Performance"
    subtitle="On-Time vs Late"
  >
    

    <div className="text-center">

      <div className="text-4xl font-bold text-red-500">
        {dashboardSummary.deliveryScore ?? 0}%
      </div>

      <div className="text-xs text-gray-500">
        On-Time Rate
        <div className="text-xs text-gray-500 mt-2">
  Total Deliveries:
  {(dashboardSummary.deliveryTotal || 0).toLocaleString()}
</div>
      </div>

    </div>

    <div className="mt-4">

      <div
  className="flex justify-between text-xs mb-1 cursor-pointer hover:text-green-600"
  onClick={() =>
    navigate('/vendor-management/delivery-performance/list?type=ontime')
  }
>
  
  <span>On-Time</span>

  <span>
    {(dashboardSummary.onTimeDeliveries || 0).toLocaleString()}
  </span>
</div>

      <div className="h-2 bg-gray-200 rounded mb-3">
        <div
          className="h-2 bg-green-500 rounded"
          style={{width: `${Number(dashboardSummary.deliveryScore || 0)}%`}}
        />
      </div>

      <div
  className="flex justify-between text-xs mb-1 cursor-pointer hover:text-red-600"
  onClick={() =>
    navigate('/vendor-management/delivery-performance/list?type=late')
  }
>
  <span>Late</span>

  <span>
    {(dashboardSummary.lateDeliveries || 0).toLocaleString()}
  </span>
</div>

      <div className="h-2 bg-gray-200 rounded">
        <div
          className="h-2 bg-red-500 rounded"
          style={{width: `${100 - Number(dashboardSummary.deliveryScore || 0)}%`}}
        />
      </div>

    </div>

  </KPISection>

  {/* 4 Quality */}

  <KPISection
    number="4"
    title="Quality Inspection Score"
    subtitle="Rework Rate"
    score={dashboardSummary.qualityScore ?? 0}
  >

    <div className="grid grid-cols-2 gap-3">

  <div
    className="bg-gray-50 p-3 rounded-lg cursor-pointer"
    onClick={() =>
      navigate("/vendor-management/quality-list?type=accepted")
    }
  >
    <div className="text-xs text-gray-500">
      Accepted GRPO
    </div>

    <div className="font-bold text-lg">
      {dashboardSummary.acceptedGRPO ?? 0}
    </div>
  </div>

  <div
    className="bg-gray-50 p-3 rounded-lg cursor-pointer"
    onClick={() =>
      navigate("/vendor-management/quality-list?type=rework")
    }
  >
    <div className="text-xs text-gray-500">
      Rework Orders
    </div>

    <div className="font-bold text-lg text-orange-500">
      {dashboardSummary.reworkCount ?? 0}
    </div>
  </div>



    </div>

  </KPISection>

  {/* 5 Procurement */}

  <KPISection
    number="5"
    title="Procurement Failures"
    subtitle="Open PO Risk"
    score={dashboardSummary.procurementScore ?? 0}
  >

    <div className="grid grid-cols-2 gap-3">

  <div
    className="bg-gray-50 p-3 rounded-lg cursor-pointer"
    onClick={() =>
      navigate("/vendor-management/procurement-list?type=closed")
    }
  >
    <div className="text-xs text-gray-500">
      Closed POs
    </div>

    <div className="font-bold text-lg">
      {dashboardSummary.closedPO ?? 0}
    </div>
  </div>

  <div
    className="bg-gray-50 p-3 rounded-lg cursor-pointer"
    onClick={() =>
      navigate("/vendor-management/procurement-list?type=open")
    }
  >
    <div className="text-xs text-gray-500">
      Open POs
    </div>

    <div className="font-bold text-lg text-orange-500">
      {dashboardSummary.openPO ?? 0}
    </div>
  </div>

</div>

  </KPISection>

  {/* 6 Financial */}

  <div
  className="cursor-pointer"
  onClick={() =>
    navigate(
      "/vendor-management/financial-list?type=matched"
    )
  }
>
  <KPISection
    number="6"
    title="Financial Stability"
    subtitle="AP Invoice Risk"
    score={dashboardSummary.financialScore ?? 0}
  >

    <div className="grid grid-cols-2 gap-3">

      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="text-xs">
          Closed Invoices
        </div>

        <div className="font-bold text-lg">
          {dashboardSummary.closedInvoices ?? 0}
        </div>
      </div>

      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="text-xs">
          Open Invoices
        </div>

        <div className="font-bold text-lg text-orange-500">
          {dashboardSummary.openInvoices ?? 0}
        </div>
      </div>

    </div>

  </KPISection>
</div>

</div>




      
      

      <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-sap-border/60 text-left text-[11px] uppercase tracking-wide text-sap-textLight">
                <th className="px-4 py-3 font-semibold">Vendor</th>
                <th className="px-4 py-3 font-semibold text-right">
                  <button type="button" onClick={() => toggleSort('risk_score')} className="hover:text-sap-text">
                    Risk score {sort === 'risk_score' && (dir === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button type="button" onClick={() => toggleSort('risk_level')} className="hover:text-sap-text">
                    Risk level {sort === 'risk_level' && (dir === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold text-right">Vendor score</th>
                
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sap-textLight text-xs">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sap-textLight text-xs">No vendors match filters</td></tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.vendor_id || row.Vendor}
                    className="border-t border-sap-border/40 hover:bg-sap-blue/5 cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/vendor-management/risk/${encodeURIComponent(row.Vendor || row.vendor_name)}`
                      )
                    }
                  >
                  
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-sap-blue">{row.vendor_name || row.Vendor}</div>
                      <div className="text-[10px] text-sap-textLight">{row.vendor_type}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold">{Number(row.risk_score || row.Final_Risk_Score || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5"><RiskBadge level={(row.risk_level || row.Risk_Status || '').replace(' RISK', '')} /></td>
                    <td className="px-4 py-2.5 text-right">{Number(row.vendor_score || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-center">
                   
</td>
                  </tr>



                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-sap-border/60 flex flex-wrap items-center justify-between gap-3 text-xs text-sap-textLight">
          <span>Showing {from}-{to} of {total}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">‹</button>
            <span>{page}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">›</button>
          </div>
          <label className="flex items-center gap-2">
            Rows per page
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1 bg-white">
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
      </div>

  </div>
    
  );
}
